import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Container,
  Typography,
  useMediaQuery,
  Grid,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

import { GUIDE_SERVER } from '../../Config';

axios.defaults.withCredentials = true;

const extConfig = {
  '.pdf': {
    icon: <PictureAsPdfIcon sx={{ color: '#e53935' }} />,
    label: 'PDF',
    color: '#e53935',
  },
};

const GuidelinePage = () => {
  const matches = useMediaQuery('(max-width:768px)');
  const [fileList, setFileList] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerError, setViewerError] = useState('');
  const [pdfBlobUrl, setPdfBlobUrl] = useState('');

  const transform = (slot) => ({
    ...slot,
    Open: () => <></>,
    OpenMenuItem: () => <></>,
    Download: () => <></>,
    DownloadMenuItem: () => <></>,
  });

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    renderToolbar: (Toolbar) => (
      <Toolbar>{defaultLayoutPluginInstance.toolbarPluginInstance.renderDefaultToolbar(transform)}</Toolbar>
    ),
  });

  const apiFileUrl = useMemo(() => {
    if (!selectedFile) return '';
    return `${process.env.REACT_APP_SERVER}${GUIDE_SERVER}/file/${encodeURIComponent(selectedFile.name)}`;
  }, [selectedFile]);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_SERVER}${GUIDE_SERVER}/list`, {
        withCredentials: true,
      })
      .then((res) => {
        const files = Array.isArray(res.data) ? res.data : [];
        setFileList(files);

        if (files.length > 0) {
          setSelectedFile(files[0]);
        }
      })
      .catch((err) => {
        console.error('guideline list fetch error:', err);
        setFileList([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!selectedFile || !apiFileUrl) {
      setPdfBlobUrl('');
      return;
    }

    let objectUrl = '';

    const fetchPdf = async () => {
      try {
        setViewerError('');
        setViewerLoading(true);

        const response = await axios.get(apiFileUrl, {
          responseType: 'blob',
          withCredentials: true,
        });

        const blob = new Blob([response.data], { type: 'application/pdf' });
        objectUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(objectUrl);
      } catch (error) {
        console.error('pdf axios fetch error:', error);
        setViewerError('PDF 문서를 불러오지 못했습니다.');
        setPdfBlobUrl('');
      } finally {
        setViewerLoading(false);
      }
    };

    fetchPdf();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [apiFileUrl, selectedFile]);

  const handleSelect = (file) => {
    setViewerError('');
    setSelectedFile(file);
  };

  const handleDownload = async () => {
    if (!selectedFile || !apiFileUrl) return;

    try {
      const response = await axios.get(apiFileUrl, {
        responseType: 'blob',
        withCredentials: true,
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', selectedFile.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('pdf download error:', error);
      setViewerError('PDF 다운로드에 실패했습니다.');
    }
  };

  return (
    <div className="GuidelinePage">
      <Container
        maxWidth="xl"
        sx={matches ? { paddingTop: '116px', paddingBottom: '100px' } : { paddingTop: '188px', paddingBottom: '100px' }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={700} color="#1f2937">
            가이드라인 / 매뉴얼
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            파일을 선택해서 바로 확인할 수 있습니다.
          </Typography>
        </Box>

        <Grid container spacing={2} sx={{ height: 'calc(100vh - 320px)', minHeight: '600px' }}>
          <Grid size={{ xs: 12, md: 3 }}>
            <Box
              sx={{
                height: '100%',
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                backgroundColor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f3f4f6', backgroundColor: '#f8fafc' }}>
                <Typography variant="subtitle2" fontWeight={700} color="#374151">
                  파일 목록
                </Typography>
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : fileList.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, p: 2 }}>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    등록된 가이드라인 / 매뉴얼 파일이 없습니다.
                  </Typography>
                </Box>
              ) : (
                <List dense disablePadding sx={{ overflowY: 'auto', flex: 1 }}>
                  {fileList.map((file, idx) => {
                    const cfg = extConfig[file.ext] || extConfig['.pdf'];
                    const isSelected = selectedFile?.name === file.name;

                    return (
                      <ListItem key={idx} disablePadding divider>
                        <ListItemButton
                          selected={isSelected}
                          onClick={() => handleSelect(file)}
                          sx={{
                            '&.Mui-selected': {
                              backgroundColor: '#fef2f2',
                              borderLeft: `3px solid ${cfg.color}`,
                            },
                            '&.Mui-selected:hover': {
                              backgroundColor: '#fee2e2',
                            },
                            py: 1.2,
                            height: '64px',
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>{cfg.icon}</ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                fontWeight={isSelected ? 600 : 400}
                                sx={{ wordBreak: 'break-all', lineHeight: 1.4, fontSize: '13px' }}
                              >
                                {file.name}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 9 }}>
            <Box
              sx={{
                height: '100%',
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                backgroundColor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: '1px solid #f3f4f6',
                  backgroundColor: '#f8fafc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} color="#374151" noWrap>
                  {selectedFile ? selectedFile.name : '파일을 선택하세요'}
                </Typography>

                {selectedFile && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2, flexShrink: 0 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownload}
                      sx={{ textTransform: 'none', fontSize: '12px' }}
                    >
                      다운로드
                    </Button>

                    <Button
                      size="small"
                      variant="outlined"
                      endIcon={<OpenInNewIcon />}
                      href={apiFileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textTransform: 'none', fontSize: '12px' }}
                    >
                      새 탭에서 열기
                    </Button>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  backgroundColor: '#f8fafc',
                  position: 'relative',
                }}
              >
                {!selectedFile ? (
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#9ca3af',
                      gap: 1,
                    }}
                  >
                    <PictureAsPdfIcon sx={{ fontSize: 56, opacity: 0.3 }} />
                    <Typography variant="body2" color="text.secondary">
                      좌측 목록에서 PDF 파일을 선택하세요
                    </Typography>
                  </Box>
                ) : viewerError ? (
                  <Box sx={{ p: 3 }}>
                    <Alert severity="warning">{viewerError}</Alert>
                  </Box>
                ) : (
                  <Box sx={{ height: '100%' }}>
                    {viewerLoading && (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          zIndex: 10,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255,255,255,0.65)',
                        }}
                      >
                        <CircularProgress size={32} />
                      </Box>
                    )}

                    {pdfBlobUrl && (
                      <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                        <Viewer
                          fileUrl={pdfBlobUrl}
                          plugins={[defaultLayoutPluginInstance]}
                          defaultScale={0.95}
                          onDocumentLoadFail={(error) => {
                            console.error('pdf load error:', error);
                            setViewerError('PDF 문서를 불러오지 못했습니다.');
                          }}
                        />
                      </Worker>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default GuidelinePage;
